import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { User } from "firebase/auth";
import { getAuthClient, loadAuthModule } from "@/lib/firebase/auth";
import { firebaseIdle } from "@/lib/firebase/app";
import { setBtwPreference } from "@/lib/btw";
import { setAnalyticsUserId } from "@/lib/consent";
import { translate } from "@/lib/i18n";
import { getProfile, saveProfile } from "./profile";

/** Profielgegevens die bij het aanmaken van een account meteen worden opgeslagen. */
export interface RegisterProfile {
  firstName: string;
  lastName: string;
  /** Verplicht bij registratie: eigen transport meldt de bezorgdag per sms. */
  phone: string;
  isBusiness: boolean;
  companyName?: string;
  kvk?: string;
  vatNumber?: string;
  /** Uitkomst van de VIES-btw-check (door de registratiepagina meegegeven). */
  businessStatus?: "verified" | "pending";
  businessVerifiedName?: string;
}

interface CustomerAuth {
  user: User | null;
  ready: boolean;
  /** True zodra de auth-status bekend is én er een ingelogde gebruiker is. */
  signedIn: boolean;
  verified: boolean;
  login: (email: string, password: string) => Promise<unknown>;
  register: (email: string, password: string, profile?: RegisterProfile) => Promise<User>;
  logout: () => Promise<void>;
  sendReset: (email: string) => Promise<void>;
  resendVerification: () => Promise<void>;
}

const Ctx = createContext<CustomerAuth | null>(null);

/** Globale klant-auth (Firebase e-mail/wachtwoord). Eén onAuthStateChanged-listener. */
export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // De auth-listener trekt Firebase op. Dat stellen we uit tot de browser idle is,
    // zodat het niet met de eerste render concurreert. Tot die tijd blijft `ready`
    // false → beveiligde routes tonen "Account laden…" (geen bounce).
    let unsub: (() => void) | undefined;
    let cancelled = false;
    void firebaseIdle().then(async () => {
      if (cancelled) return;
      const [{ onAuthStateChanged, signOut }, auth] = await Promise.all([loadAuthModule(), getAuthClient()]);
      if (cancelled) return;
      unsub = onAuthStateChanged(auth, (u) => {
        setUser(u);
        setReady(true);
        // GA4 Gebruikers-ID volgt de inlogstatus: dezelfde klant op telefoon én laptop telt zo als
        // één gebruiker in plaats van twee. Alleen de uid — nooit het e-mailadres.
        setAnalyticsUserId(u?.uid);
        // Btw-voorkeur volgt het account: bij inloggen uit het profiel, bij uitloggen terug naar incl.
        if (u) {
          // Controleer of het account in Firebase nog bestaat/actief is: een GEFORCEERDE
          // token-refresh faalt als de gebruiker verwijderd of uitgeschakeld is (het lokaal
          // bewaarde token blijft anders tot een uur geldig). Faalt 'ie → meteen uitloggen,
          // zodat een verwijdering in Firebase direct doorwerkt op de site.
          u.getIdToken(true).catch(() => { void signOut(auth); });
          getProfile(u.uid).then((p) => setBtwPreference(p.btwPreference)).catch(() => {});
        } else {
          setBtwPreference("incl");
        }
      });
    });
    return () => { cancelled = true; unsub?.(); };
  }, []);

  const value = useMemo<CustomerAuth>(() => ({
    user,
    ready,
    signedIn: ready && !!user,
    verified: !!user?.emailVerified,
    login: async (email, password) => {
      const [{ signInWithEmailAndPassword }, auth] = await Promise.all([loadAuthModule(), getAuthClient()]);
      return signInWithEmailAndPassword(auth, email, password);
    },
    register: async (email, password, profile) => {
      const [{ createUserWithEmailAndPassword, sendEmailVerification, updateProfile }, auth] = await Promise.all([loadAuthModule(), getAuthClient()]);
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      // Nieuw account = een belangrijke gebeurtenis (GA4 `sign_up`). Lui geladen zodat de
      // meetmodule niet in het inlogscherm meebundelt; een mislukte meting mag registreren
      // nooit tegenhouden.
      void import("@/lib/track").then((m) => m.trackAccountSignup()).catch(() => undefined);
      if (profile) {
        const displayName = `${profile.firstName} ${profile.lastName}`.trim();
        if (displayName) await updateProfile(cred.user, { displayName });
        const btwPreference = profile.isBusiness ? "excl" : "incl";
        // Best-effort: het profiel meteen wegschrijven (blokkeert de registratie niet).
        await saveProfile(cred.user.uid, {
          firstName: profile.firstName,
          lastName: profile.lastName,
          phone: profile.phone,
          isBusiness: profile.isBusiness,
          companyName: profile.companyName ?? "",
          kvk: profile.kvk ?? "",
          vatNumber: profile.vatNumber ?? "",
          ...(profile.isBusiness
            ? { businessStatus: profile.businessStatus ?? "pending", businessVerifiedName: profile.businessVerifiedName ?? "", businessVerifiedAt: Date.now() }
            : {}),
          btwPreference,
        }).catch(() => {});
        setBtwPreference(btwPreference);
      }
      await sendEmailVerification(cred.user);
      return cred.user;
    },
    logout: async () => {
      const [{ signOut }, auth] = await Promise.all([loadAuthModule(), getAuthClient()]);
      return signOut(auth);
    },
    sendReset: async (email) => {
      const [{ sendPasswordResetEmail }, auth] = await Promise.all([loadAuthModule(), getAuthClient()]);
      return sendPasswordResetEmail(auth, email);
    },
    resendVerification: async () => {
      const [{ sendEmailVerification }, auth] = await Promise.all([loadAuthModule(), getAuthClient()]);
      const u = auth.currentUser;
      if (u) await sendEmailVerification(u);
    },
  }), [user, ready]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCustomerAuth(): CustomerAuth {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCustomerAuth moet binnen <CustomerAuthProvider> staan");
  return ctx;
}

/** Vertaal Firebase-auth-foutcodes naar nette NL-meldingen. */
export function authErrorMessage(err: unknown): string {
  const code = (err as { code?: string })?.code ?? "";
  switch (code) {
    case "auth/invalid-email": return translate("Dit e-mailadres lijkt niet te kloppen.");
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found": return translate("E-mailadres of wachtwoord klopt niet.");
    case "auth/email-already-in-use": return translate("Er bestaat al een account met dit e-mailadres. Log in of reset uw wachtwoord.");
    case "auth/weak-password": return translate("Kies een wachtwoord van minstens 6 tekens.");
    case "auth/too-many-requests": return translate("Te veel pogingen. Probeer het over een paar minuten opnieuw.");
    case "auth/network-request-failed": return translate("Geen verbinding. Controleer uw internet en probeer opnieuw.");
    default: return translate("Er ging iets mis. Probeer het opnieuw.");
  }
}
